import Box from '@material-ui/core/Box';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import Paper from '@material-ui/core/Paper';
import Radio from '@material-ui/core/Radio';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/core/styles';
import Tab from '@material-ui/core/Tab';
import Tabs from '@material-ui/core/Tabs';
import * as moment from 'moment';
import React, { useMemo } from 'react';
import { useLanguage } from '../../../../../../context/language/language';
import useResponsive from '../../../../../../utils/responsive';
import useDeliveryData from '../../../../../common/hook/useDeliveryData';
import Display from '../../../../../common/Display';
import useDeliverySlot from '../../../../../common/hook/useDeliverySlot';
import { number2Decimal } from '../../../../../../utils/productUtil';
import { useSite } from '../../../../../../context/siteContext';
import { getImageURL } from '../../../../../../utils/getImageURL';

const EmptySlot = getImageURL('/images/empty_slot.png');

const useStyles = makeStyles((theme) => ({
  root: {
    flexGrow: 1,
    width: '100%',
  },
  TimeColumn: {
    display: 'flex',
    flexDirection: 'column',
    overflowY: 'scroll',
    maxHeight: ({ containerHeight }) => containerHeight,

    '-ms-overflow-style': 'none',
    'scrollbar-width': 'none',
    '&::-webkit-scrollbar': {
      display: 'none',
    }
  },
  TimeRow: {
    display: 'flex',
    flexDirection: 'column',
  },
  ListRoot: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gridColumnGap: 16,
    padding: '12px 0',

    [theme.breakpoints.down('xs')]: {
      gridTemplateColumns: 'repeat(1, 1fr)',
      padding: 0,
    }
  },
  ListItem: ({ slotLength }) => ({
    width: '100%',
    minHeight: 48,
    borderBottom: slotLength > 2 ? '0.5px solid #E0E0E0' : 'unset',
    padding: 0,
    paddingTop: 12,
    paddingBottom: 12,
    alignItems: 'flex-start',

    [theme.breakpoints.down('xs')]: {
      minHeight: 56,
      paddingTop: 16,
      paddingBottom: 16,

      '&:last-child': {
        borderBottom: 'unset',
      }
    }
  }),
  Divider: {
    margin: '0px 10px',
  },
  Price: {
    justifyContent: 'flex-end',
    color: theme.custom.colors.grey700,
    fontSize: '16px !important',
    fontWeight: 500,
    lineHeight: '24px',
    textAlign: 'end',
  },
  emptySlotIcon: {
    width: 100,
    height: 100,
    paddingTop: 18
  },
  emptyText: {
    ...theme.fonts.textL,
    textAlign: 'center',
  },
  customTabItem: {
    'border-bottom': `solid 1px ${theme.custom.colors.grey300}`,
    padding: '0px',
    minWidth: 55,
    width: 55,
    marginRight: 4,

    [theme.breakpoints.down('xs')]: {
      marginRight: 8,
    },

    '&:last-child': {
      marginRight: 0,
    }
  },
  customTabItemMobile: {
    'border-bottom': `solid 1px ${theme.custom.colors.grey300}`,
    minWidth: 95,
    width: 95,

    [theme.breakpoints.down('xs')]: {
      marginRight: 8,
    },

    '&:last-child': {
      marginRight: 0,
    }
  },
  customTabLabel: {
    ...theme.fonts.textS,
    textTransform: 'none',
  },
  timeText: {
    ...theme.fonts.textS,
    color: theme.custom.colors.grey550,
    lineHeight: '24px',
    [theme.breakpoints.only('md')]: {
      fontSize: 16,
    },
    [theme.breakpoints.down('xs')]: {
      ...theme.fonts.textL
    }
  },
  timeText2: {
    ...theme.fonts.textSS,
    color: theme.custom.colors.grey550,
    [theme.breakpoints.only('md')]: {
      fontSize: 10,
    }
  },
  tabHead: {
    ...theme.fonts.textS,
    [theme.breakpoints.down('xs')]: {
      ...theme.fonts.textM
    }
  },
  tabBody: {
    ...theme.fonts.header5,
    [theme.breakpoints.down('xs')]: {
      ...theme.fonts.header4
    }
  },
  radioStyle: {
    color: theme.custom.colors.grey300,
    padding: 0,
    marginRight: 8,
  },
  listItemIcon: {
    minWidth: 'unset',
    [theme.breakpoints.down('xs')]: {
      minWidth: '20px'
    }
  }
}));

const EmptySlotSection = () => {
  const classes = useStyles();
  const word = useLanguage();
  return (
    <Grid
      container
      direction="column"
      justify="flex-start"
      alignItems="center"
    >
      <Grid item>
        <img src={EmptySlot} alt="empty slot" className={classes.emptySlotIcon} />
      </Grid>
      <Grid item>
        <Typography variant="body1" className={classes.emptyText}>
          {word('Slot.Error.Notfound')}
        </Typography>
      </Grid>
    </Grid>
  );
};

function TabPanel(props) {
  const {
    children,
    value,
    index,
    slotData,
    handleOptionChange,
    slotIdChecked,
    slot,
    containerHeight,
    ...other
  } = props;
  const classes = useStyles({ slotLength: slotData?.length, containerHeight: containerHeight - 80 });
  const word = useLanguage();
  const deliveryData = useDeliveryData();
  const { MySite } = useSite();

  const getSlotFreeMessage = (deliveryFee = {}, isAvailable = false) => {
    if (isAvailable) {
      if (deliveryData?.deliveryMethod === 'B2B') {
        if (word("B2B.Slot.Free.Message") === "B2B.Slot.Free.Message" || deliveryFee?.value <= 0) {
          return undefined;
        }
        return word("B2B.Slot.Free.Message");
      }
      if (deliveryFee?.value > 0 && word("Slot.Free.Message") !== "Slot.Free.Message") {
        return word("Slot.Free.Message");
      }
    }
    return undefined;
  };

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`scrollable-auto-tabpanel-${index}`}
      aria-labelledby={`scrollable-auto-tab-${index}`}
      {...other}
      className={classes.TimeColumn}
    >
      {value === index && (
        <List
          component="nav"
          className={classes.ListRoot}
          aria-label="mailbox folders"
        >
          {slotData.map((val, idx) => {
            const {
              isAvailable: _isAvailable,
              fromTime,
              toTime,
              cutOffTime,
              deliveryFee,
              slotId,
              defaultSlot,
            } = val;
            let textItem = '';
            let priceUnit;
            let description;
            let validTime = false;
            let isAvailable = _isAvailable;
            priceUnit = deliveryFee && deliveryFee.value > 0
              ? (deliveryFee.currency === 'THB' ? '฿' : 'RM')
              + (deliveryFee.currency === 'THB' ? deliveryFee.value : number2Decimal(deliveryFee.value, 2))
              : word('Slot.Surcharge.Free');
            if (cutOffTime) {
              validTime = moment().isBefore(`${slot.date} ${cutOffTime}`);
              if (!validTime) {
                isAvailable = false;
              } else if (!_isAvailable) {
                priceUnit = word('Slot.Full');
              }
            }
            if (defaultSlot) {
              if (deliveryData?.deliveryMethod?.startsWith('HD')) {
                const today = moment().format('YYYY-MM-DD');
                if (slot.date === today) {
                  textItem = word('Slot.Default.Homedelivery.Today');
                }
                textItem = word('Slot.Default.Homedelivery.Tomorrow');
                description = word('Slot.HomeDelivery.NoSlot.Note');
              } else if (deliveryData?.deliveryMethod?.startsWith('CC')) {
                textItem = word('Slot.Default.PickUp');
                description = word('Slot.CollectAtStore.NoSlot.Note');
              }
            } else if (fromTime && toTime) {
              textItem = `${fromTime} - ${toTime}`;
            } else {
              textItem = '';
            }

            return (
              <ListItem disabled={!isAvailable} className={classes.ListItem}>
                <Radio
                  id={`book-slot-id-${idx}`}
                  checked={slotId === slotIdChecked}
                  disabled={!isAvailable}
                  value={slotId}
                  onChange={() => handleOptionChange(slotId)}
                  classes={{ root: classes.radioStyle }}
                />
                <div>
                  <div className={classes.timeText}>{textItem}</div>
                  {description && <div className={classes.timeText2}>{description}</div>}
                </div>
                <ListItemText primary="" />
                <div className={classes.Price}>
                  {priceUnit}
                  {MySite ? <br /> : ' '}
                  {getSlotFreeMessage(deliveryFee, isAvailable)}
                </div>
              </ListItem>
            );
          })}
          <Display show={slotData.length === 0}>
            <EmptySlotSection />
          </Display>
        </List>
      )}
    </div>
  );
}

export default function TimeSlot({
  collectionSlot = [],
  handleOptionChange,
  slotId = null,
  containerHeight,
}) {
  const word = useLanguage();
  const { ThSite } = useSite();
  const { isMobile } = useResponsive();
  const deliverySlot = useDeliverySlot();
  const classes = useStyles();
  const [value, setValue] = React.useState(0);
  const slotTime = useMemo(() => {
    const todayText = moment().zone(ThSite ? '+07:00' : '+08:00').format('YYYY-MM-DD');
    if (!collectionSlot.length) return [];

    if (ThSite) {
      try {
        const dateDiff = moment(collectionSlot[0].date).zone('+07:00').diff(todayText, 'days');
        if (dateDiff > 1) {
          return [];
        }
      } catch (e) {
        return [];
      }
    }
    return collectionSlot;
  }, [collectionSlot, slotId]);

  const tabs = useMemo(() => {
    if (collectionSlot.length === undefined) return [];
    const todayText = moment().zone(ThSite ? '+07:00' : '+08:00').format('YYYY-MM-DD');
    const useSlot = collectionSlot.slice(0, 7)
      .map((x) => {
        const dateDiff = moment(x.date).diff(todayText, 'days');
        if (dateDiff <= 7) {
          return {
            ...x,
            diffDate: dateDiff
          };
        }
        return null;
      })
      .filter((x) => x);
    if (ThSite) {
      const textResult = useSlot.filter((x) => x.diffDate <= 1)
        .map((d) => (d.diffDate === 0 ? word('Slot.Date.Today') : word('Slot.Date.NextDay')));
      if (textResult.length) {
        return useSlot.map((x) => {
          const date = moment(x.date, 'YYYY-MM-DD');
          const format = isMobile ? 'D MMM' : 'D MMMM';
          return (
            <Box flexGrow={1}>
              <Box textAlign="center" className={classes.tabHead}>{textResult}</Box>
              <Box textAlign="center" className={classes.tabBody}>{date.format('D MMM')}</Box>
            </Box>
          );
        });
      }
      return [word('Slot.Date.NextDay')];
    }

    return useSlot.map((x, index) => {
      const date = moment(x.date, 'YYYY-MM-DD');
      const textResult = date.format('ddd');
      return (
        <Box flexGrow={1}>
          <Box textAlign="center" className={classes.tabHead}>{textResult}</Box>
          <Box textAlign="center" className={classes.tabBody}>{date.format('D MMM')}</Box>
        </Box>
      );
    });
  }, [collectionSlot, slotId]);

  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  return (
    <div className={classes.root}>
      <Paper elevation={0}>
        <Tabs
          eleva
          value={value}
          onChange={handleChange}
          indicatorColor="secondary"
          textColor="secondary"
          variant={
            ThSite
              ? 'fullWidth'
              : isMobile
                ? (tabs.length > 3 ? 'scrollable' : 'fullWidth')
                : (tabs.length > 7 ? 'scrollable' : 'fullWidth')
          }
          scrollButtons="auto"
        >
          {tabs.map((e, index) => (
            <Tab
              classes={{
                root: isMobile ? classes.customTabItemMobile : classes.customTabItem,
                wrapper: classes.customTabLabel
              }}
              label={e}
              className={classes.tab}
              selected={index === value}
              id={`slot-day-tab-${index}`}
            />
          ))}
        </Tabs>
      </Paper>
      <Display show={slotTime.length > 0}>
        {
          slotTime.map((slot, key) => (
            <TabPanel
              value={value}
              index={key}
              slotData={slot.slots}
              slot={slot}
              handleOptionChange={handleOptionChange}
              slotIdChecked={slotId}
              containerHeight={containerHeight}
            />
          ))
        }
      </Display>
      <Display show={slotTime.length === 0 && !deliverySlot.isLoading}>
        <EmptySlotSection />
      </Display>
    </div>
  );
}
